import styled from 'styled-components';

import { ds, dsShadow } from '@/config/colors';

import { media } from '../../../../utils/mediaQueries';
import { rise } from '../../auth.styled';
import { AUTH_TOP_BAR_HEIGHT } from '../AuthTopBar/AuthTopBar.styled';

/* Wysokość stopki (z linią), odejmowana od wysokości ekranu, żeby karta i stopka mieściły się bez przewijania. */
export const AUTH_FOOTER_HEIGHT = 57;

/*
 * Wspólny układ ekranów auth (logowanie, reset hasła, ustawienie hasła, weryfikacja, zaproszenie).
 * Desktop: po lewej krótki tekst na bieli, po prawej zdjęcie na pół ekranu,
 * a biała karta z formularzem leży na granicy obu stron. Kolumny 1fr | karta | 1fr są symetryczne,
 * więc środek karty wypada dokładnie na krawędzi zdjęcia (left: 50%).
 * Tablet: zdjęcie na całe tło, karta na środku, bez tekstu. Telefon: sam formularz na bieli.
 */
export const AuthPage = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  background: ${ds.bgSurface};
`;

export const Hero = styled.main`
  position: relative;
  flex: 1;
  display: flex;
  min-height: calc(100dvh - ${AUTH_TOP_BAR_HEIGHT}px - ${AUTH_FOOTER_HEIGHT}px);
  overflow: hidden;

  ${media.mobile} {
    min-height: auto;
  }
`;

export const PhotoPane = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 50%;
  background: ${ds.bgMuted};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  ${media.tablet} {
    left: 0;
  }

  ${media.mobile} {
    display: none;
  }
`;

export const HeroGrid = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 48px 32px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 440px minmax(0, 1fr);
  align-items: center;
  gap: 48px;

  ${media.tabletDown} {
    grid-template-columns: minmax(0, 440px);
    justify-content: center;
  }

  ${media.mobile} {
    grid-template-columns: minmax(0, 1fr);
    padding: 32px 20px 40px;
    align-items: start;
  }
`;

export const Intro = styled.section`
  max-width: 420px;
  ${rise(60)}

  ${media.tabletDown} {
    display: none;
  }

  ${media.reducedMotion} {
    animation: none;
  }
`;

export const IntroTitle = styled.h2`
  margin: 0 0 12px;
  /* Skala z szerokością ekranu, żeby każda z dwóch linii (pl/en/de) mieściła się bez łamania. */
  font-size: 26px;
  font-weight: 500;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: ${ds.textPrimary};
`;

/* Pisany tekst powitania: dwie linie, zarezerwowana wysokość, żeby układ nie skakał. */
export const TypingText = styled.span`
  display: block;
  min-height: calc(2em * 1.25);
  white-space: pre;
`;

export const TypingCaret = styled.span`
  display: inline-block;
  width: 2px;
  height: 0.95em;
  margin-left: 3px;
  vertical-align: -0.1em;
  background: currentColor;
  animation: auth-caret-blink 1s steps(1) infinite;

  @keyframes auth-caret-blink {
    50% {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const IntroLead = styled.p`
  margin: 0;
  max-width: 360px;
  font-size: 15px;
  line-height: 1.55;
  text-wrap: pretty;
  color: ${ds.textSecondary};
`;

/* Karta z formularzem: r/12, cień modalny, bo unosi się nad zdjęciem.
   Efekt szkła: półprzezroczysta biel z rozmyciem, więc zdjęcie prześwituje miękko przez prawą
   część karty, a nad białą stroną karta pozostaje czysto biała. Bez obsługi backdrop-filter
   tło jest pełną bielą. Na telefonie zwykła biała płaszczyzna bez ramki i cienia. */
export const FormCard = styled.div`
  width: 100%;
  padding: 40px;
  background: ${ds.bgSurface};
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  box-shadow: ${dsShadow.modal}, inset 0 0 0 1px rgba(15, 23, 42, 0.04);
  ${rise(120)}

  @supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    background: rgba(255, 255, 255, 0.78);
    -webkit-backdrop-filter: blur(12px) saturate(140%);
    backdrop-filter: blur(12px) saturate(140%);
  }

  ${media.mobile} {
    padding: 0;
    background: ${ds.bgSurface};
    border: none;
    border-radius: 0;
    box-shadow: none;
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }

  ${media.reducedMotion} {
    animation: none;
  }
`;

/* Stopka ekranów auth: prawa autorskie po lewej, linki do dokumentów po prawej. */
export const AuthFooter = styled.footer`
  position: relative;
  z-index: 2;
  background: ${ds.bgSurface};
  border-top: 1px solid ${ds.borderDefault};
`;

export const AuthFooterInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: ${AUTH_FOOTER_HEIGHT - 1}px;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 32px;
  font-size: 13px;
  color: ${ds.textSecondary};

  ${media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px 20px;
  }
`;

export const AuthFooterLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;

  a {
    color: ${ds.textSecondary};
    text-decoration: none;
    text-underline-offset: 3px;

    &:hover {
      color: ${ds.textPrimary};
      text-decoration: underline;
    }
  }
`;

/* Zmiana języka w stopce: tekstowy przycisk w kolorze stopki, podkreślenie na hover. */
export const FooterLanguageButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  font-size: 13px;
  font-weight: 500;
  color: ${ds.textPrimary};
  background: transparent;
  border: none;
  cursor: pointer;
  text-underline-offset: 3px;

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 1.75;
    color: ${ds.textSecondary};
  }

  &:hover span {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${ds.accentInk};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const AuthFooterEnd = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
`;

/* Wideo w tle (jak hero landingu): poster od razu, samo wideo dociągane po załadowaniu strony. */
export const PhotoVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
